// ===========================================
// Custom Fields Builder Component
// ===========================================
// Allows admins to add/edit/delete/reorder custom registration fields

import { useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  ArrowUpward as UpIcon,
  ArrowDownward as DownIcon,
} from '@mui/icons-material';
import type { CustomRegistrationField } from '../../../api/adminSettings.api';

// ===========================================
// TYPES
// ===========================================

interface CustomFieldsBuilderProps {
  fields: CustomRegistrationField[];
  onChange: (fields: CustomRegistrationField[]) => void;
  maxFields?: number;
}

const FIELD_TYPES: { value: CustomRegistrationField['type']; label: string }[] = [
  { value: 'text', label: 'Text (single line)' },
  { value: 'textarea', label: 'Text (multi-line)' },
  { value: 'select', label: 'Dropdown' },
  { value: 'checkbox', label: 'Checkbox' },
  { value: 'radio', label: 'Radio Buttons' },
];

const TYPE_LABELS: Record<string, string> = {
  text: 'Text',
  textarea: 'Multi-line',
  select: 'Dropdown',
  checkbox: 'Checkbox',
  radio: 'Radio',
};

// ===========================================
// FIELD EDITOR DIALOG
// ===========================================

interface FieldEditorProps {
  open: boolean;
  field: Partial<CustomRegistrationField> | null;
  onSave: (field: CustomRegistrationField) => void;
  onCancel: () => void;
  isNew: boolean;
}

function FieldEditorDialog({ open, field, onSave, onCancel, isNew }: FieldEditorProps) {
  const [label, setLabel] = useState(field?.label ?? '');
  const [type, setType] = useState<CustomRegistrationField['type']>(field?.type ?? 'text');
  const [required, setRequired] = useState(field?.required ?? false);
  const [options, setOptions] = useState<string[]>(field?.options ?? []);
  const [newOption, setNewOption] = useState('');

  const needsOptions = type === 'select' || type === 'radio';
  const isValid = label.trim().length > 0 && (!needsOptions || options.length >= 2);

  const handleSave = () => {
    onSave({
      id: field?.id ?? `field_${Date.now()}`,
      label: label.trim(),
      type,
      required,
      options: needsOptions ? options : undefined,
      sortOrder: field?.sortOrder ?? 0,
    });
  };

  const handleAddOption = () => {
    if (newOption.trim() && options.length < 20) {
      setOptions([...options, newOption.trim()]);
      setNewOption('');
    }
  };

  const handleRemoveOption = (index: number) => {
    setOptions(options.filter((_, i) => i !== index));
  };

  return (
    <Dialog open={open} onClose={onCancel} maxWidth="sm" fullWidth>
      <DialogTitle>{isNew ? 'Add Custom Field' : 'Edit Custom Field'}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            label="Question / Label"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            fullWidth
            placeholder="e.g., Has your child had prior music lessons?"
            inputProps={{ maxLength: 200 }}
          />

          <FormControl fullWidth>
            <InputLabel>Field Type</InputLabel>
            <Select
              value={type}
              label="Field Type"
              onChange={(e) => setType(e.target.value as CustomRegistrationField['type'])}
            >
              {FIELD_TYPES.map((ft) => (
                <MenuItem key={ft.value} value={ft.value}>
                  {ft.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControlLabel
            control={
              <Switch checked={required} onChange={(e) => setRequired(e.target.checked)} />
            }
            label="Required field"
          />

          {needsOptions && (
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Options {options.length < 2 && '(minimum 2 required)'}
              </Typography>
              <Stack spacing={1}>
                {options.map((opt, i) => (
                  <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body2" sx={{ flex: 1 }}>
                      {opt}
                    </Typography>
                    <IconButton size="small" onClick={() => handleRemoveOption(i)}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                ))}
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <TextField
                    size="small"
                    value={newOption}
                    onChange={(e) => setNewOption(e.target.value)}
                    placeholder="Add option..."
                    onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddOption())}
                    sx={{ flex: 1 }}
                    inputProps={{ maxLength: 100 }}
                  />
                  <Button
                    size="small"
                    onClick={handleAddOption}
                    disabled={!newOption.trim() || options.length >= 20}
                  >
                    Add
                  </Button>
                </Box>
              </Stack>
            </Box>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel}>Cancel</Button>
        <Button onClick={handleSave} variant="contained" disabled={!isValid}>
          {isNew ? 'Add Field' : 'Save Changes'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ===========================================
// MAIN COMPONENT
// ===========================================

export default function CustomFieldsBuilder({
  fields,
  onChange,
  maxFields = 10,
}: CustomFieldsBuilderProps) {
  const [editorOpen, setEditorOpen] = useState(false);
  const [editingField, setEditingField] = useState<CustomRegistrationField | null>(null);
  const [isNew, setIsNew] = useState(false);

  const handleAdd = () => {
    setEditingField(null);
    setIsNew(true);
    setEditorOpen(true);
  };

  const handleEdit = (field: CustomRegistrationField) => {
    setEditingField(field);
    setIsNew(false);
    setEditorOpen(true);
  };

  const handleDelete = (fieldId: string) => {
    const updated = fields
      .filter((f) => f.id !== fieldId)
      .map((f, i) => ({ ...f, sortOrder: i }));
    onChange(updated);
  };

  const handleMoveUp = (index: number) => {
    if (index === 0) return;
    const updated = [...fields];
    [updated[index - 1], updated[index]] = [updated[index], updated[index - 1]];
    onChange(updated.map((f, i) => ({ ...f, sortOrder: i })));
  };

  const handleMoveDown = (index: number) => {
    if (index >= fields.length - 1) return;
    const updated = [...fields];
    [updated[index], updated[index + 1]] = [updated[index + 1], updated[index]];
    onChange(updated.map((f, i) => ({ ...f, sortOrder: i })));
  };

  const handleSave = (field: CustomRegistrationField) => {
    let updated: CustomRegistrationField[];
    if (isNew) {
      updated = [...fields, { ...field, sortOrder: fields.length }];
    } else {
      updated = fields.map((f) => (f.id === field.id ? { ...field, sortOrder: f.sortOrder } : f));
    }
    onChange(updated);
    setEditorOpen(false);
  };

  return (
    <Box>
      {fields.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          No custom fields configured. Add questions that will appear during student registration.
        </Typography>
      ) : (
        <Stack spacing={1} sx={{ mb: 2 }}>
          {fields
            .sort((a, b) => a.sortOrder - b.sortOrder)
            .map((field, index) => (
              <Card key={field.id} variant="outlined" sx={{ bgcolor: '#FCF6E6' }}>
                <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="body2" fontWeight="medium">
                        {field.label}
                        {field.required && (
                          <Typography component="span" color="error" sx={{ ml: 0.5 }}>
                            *
                          </Typography>
                        )}
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5 }}>
                        <Chip
                          label={TYPE_LABELS[field.type] || field.type}
                          size="small"
                          variant="outlined"
                        />
                        {field.options && (
                          <Chip
                            label={`${field.options.length} options`}
                            size="small"
                            variant="outlined"
                          />
                        )}
                      </Box>
                    </Box>
                    <Tooltip title="Move up">
                      <span>
                        <IconButton aria-label="Move up" size="small" onClick={() => handleMoveUp(index)} disabled={index === 0}>
                          <UpIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title="Move down">
                      <span>
                        <IconButton
                          aria-label="Move down"
                          size="small"
                          onClick={() => handleMoveDown(index)}
                          disabled={index >= fields.length - 1}
                        >
                          <DownIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title="Edit">
                      <IconButton size="small" onClick={() => handleEdit(field)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton size="small" color="error" onClick={() => handleDelete(field.id)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </CardContent>
              </Card>
            ))}
        </Stack>
      )}

      <Button
        startIcon={<AddIcon />}
        onClick={handleAdd}
        disabled={fields.length >= maxFields}
        variant="outlined"
        size="small"
      >
        Add Field {fields.length > 0 && `(${fields.length}/${maxFields})`}
      </Button>

      <FieldEditorDialog
        key={editingField?.id ?? 'new'}
        open={editorOpen}
        field={editingField}
        onSave={handleSave}
        onCancel={() => setEditorOpen(false)}
        isNew={isNew}
      />
    </Box>
  );
}
